/**
 * useCampaignProctoring — proctoring for institutional campaign assessments.
 *
 * Monitors: tab switches, window blur, copy/paste, and (optionally) head/gaze
 * direction via MediaPipe FaceMesh when a webcam MediaStream is supplied.
 *
 * Gaze counters are accumulated in a plain ref (no React state) to avoid
 * per-frame re-renders; they are merged into the sync payload every 8 s.
 *
 * Usage:
 *   const { warning, integrityScore, gazeStatus } = useCampaignProctoring({
 *     enabled: true,
 *     campaignId: "abc123",
 *     mediaStream: webcamStream,   // optional — enables gaze tracking
 *   });
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getAuthToken } from "../api";
import { isScreenShareSupported } from "../utils/proctoringSupport";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const INITIAL_SIGNALS = {
  tabSwitchCount: 0,
  windowBlurCount: 0,
  copyPasteAttempts: 0,
  gazeLookLeft: 0,
  gazeLookRight: 0,
  gazeLookDown: 0,
  faceNotVisible: 0,
  multipleFaces: 0,
  fullscreenExitCount: 0,
  screenShareStoppedCount: 0,
  multiDisplayDetected: false,
  multiDisplaySupported: false,
  multiDisplayChangedCount: 0,
  screenShareUnsupported: false,
  integrityScore: 100,
  integrityLevel: "high",
  timestamps: [],
};

const INITIAL_GAZE = {
  gazeLookLeft: 0,
  gazeLookRight: 0,
  gazeLookDown: 0,
  faceNotVisible: 0,
  multipleFaces: 0,
};

function calcScore(s) {
  let score = 100;
  score -= Math.min(30, s.tabSwitchCount * 10);
  score -= Math.min(20, s.windowBlurCount * 5);
  score -= Math.min(30, s.copyPasteAttempts * 15);
  const gazeAway = (s.gazeLookLeft || 0) + (s.gazeLookRight || 0);
  score -= Math.min(20, Math.floor(gazeAway / 3) * 5);
  score -= Math.min(10, Math.floor((s.gazeLookDown || 0) / 8) * 5);
  score -= Math.min(15, (s.faceNotVisible || 0) * 5);
  if ((s.multipleFaces || 0) > 0) score -= 20;
  score -= Math.min(20, (s.fullscreenExitCount || 0) * 10);
  score -= Math.min(30, (s.screenShareStoppedCount || 0) * 15);
  if (s.multiDisplayDetected) score -= 15;
  score -= Math.min(15, (s.multiDisplayChangedCount || 0) * 15);
  return Math.max(0, score);
}

function calcLevel(score) {
  if (score >= 75) return "high";
  if (score >= 40) return "medium";
  return "low";
}

function addTimestamp(signals, type) {
  return {
    ...signals,
    timestamps: [...(signals.timestamps || []).slice(-80), { type, at: new Date().toISOString() }],
  };
}

// ── MediaPipe FaceMesh CDN loader ──────────────────────────────────────────

function loadFaceMesh() {
  if (window.FaceMesh) return Promise.resolve(window.FaceMesh);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-mediapipe-face-mesh]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.FaceMesh));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    // Pinned, not "latest" — an unpinned CDN URL means any future upstream
    // publish (even an unrelated one) starts being served to every candidate
    // instantly with zero testing on our side. This library is also known to
    // throw async errors from deep inside its own WASM/worker internals in
    // some browser/network conditions, which bypass the try/catch below
    // entirely (it only wraps the awaited calls, not the library's own later
    // internal callbacks) — pinning at least means that failure mode is a
    // known, tested quantity instead of whatever jsdelivr happens to serve.
    script.src = "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/face_mesh.js";
    script.async = true;
    script.dataset.mediapipeFaceMesh = "true";
    script.onload = () => resolve(window.FaceMesh);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// ── Head direction from 468-point Face Mesh landmarks ─────────────────────
//
// Uses nose-tip offset relative to face width (yaw) and nose-tip height
// relative to face height (pitch). No iris landmarks needed — works with
// refineLandmarks: false for better performance.
//
// Returns: "left" | "right" | "down" | "center" | null

function computeHeadDirection(lm) {
  if (!lm[4] || !lm[234] || !lm[454] || !lm[152] || !lm[10]) return null;
  const faceW = Math.abs(lm[454].x - lm[234].x) || 0.01;
  const centerX = (lm[234].x + lm[454].x) / 2;
  const yaw = (lm[4].x - centerX) / faceW;
  const faceH = Math.abs(lm[152].y - lm[10].y) || 0.01;
  // Neutral pitch ≈ 0.45–0.52; looking down rotates chin away → pitch increases above neutral.
  // Yaw threshold 0.20 requires a clear head turn before checking pitch, preventing early exits
  // that would mask the down check.
  const pitch = (lm[4].y - lm[10].y) / faceH;
  if (yaw > 0.20) return "left";
  if (yaw < -0.20) return "right";
  if (pitch > 0.58) return "down";
  return "center";
}

// ── Hook ──────────────────────────────────────────────────────────────────

export default function useCampaignProctoring({
  enabled, campaignId, mediaStream = null, screenStream = null,
  endpoint = campaignId ? `${API_BASE}/api/institutional/campaigns/${campaignId}/proctoring` : null,
}) {
  const [signals, setSignals] = useState(INITIAL_SIGNALS);
  const [warning, setWarning] = useState("");
  const [gazeStatus, setGazeStatus] = useState("idle"); // idle|loading|active|unavailable
  // Live compliance booleans — separate from the cumulative violation
  // counters below (fullscreenExitCount / screenShareStoppedCount), which
  // only ever go up and say nothing about whether the candidate is back in
  // compliance right now. Callers that need to show/hide a "please fix
  // this" overlay should watch these, not try to infer current state from
  // a monotonically increasing count.
  const [isFullscreenActive, setIsFullscreenActive] = useState(() => !!document.fullscreenElement);
  const [isScreenShareLive, setIsScreenShareLive] = useState(true);
  const signalsRef = useRef(INITIAL_SIGNALS);
  const gazeRef = useRef({ ...INITIAL_GAZE });
  const lastSyncRef = useRef("");
  const warnTimer = useRef(null);
  const prevDirRef = useRef("center");
  const noFaceConsecRef = useRef(0);
  const multiFaceConsecRef = useRef(0);
  // Grace period: ignore blur/visibility events for the first 20 s after proctoring
  // starts. This covers the browser's "save password?" notification and the camera
  // permission prompt, both of which fire window.blur before the candidate has
  // settled into the test.
  const inGraceRef = useRef(false);
  const graceTimerRef = useRef(null);

  const showWarning = useCallback((msg) => {
    setWarning(msg);
    clearTimeout(warnTimer.current);
    warnTimer.current = setTimeout(() => setWarning(""), 6000);
  }, []);

  const update = useCallback((updater, msg = "") => {
    setSignals((cur) => {
      const base = typeof updater === "function" ? updater(cur) : updater;
      const merged = { ...base, ...gazeRef.current };
      const score = calcScore(merged);
      const result = { ...merged, integrityScore: score, integrityLevel: calcLevel(score) };
      signalsRef.current = result;
      return result;
    });
    if (msg) showWarning(msg);
  }, [showWarning]);

  // Flush gaze counters from gazeRef into React state so the displayed score
  // updates immediately when a gaze event fires (not just on the next browser event).
  const flushGaze = useCallback(() => {
    setSignals((cur) => {
      const merged = { ...cur, ...gazeRef.current };
      const score = calcScore(merged);
      const result = { ...merged, integrityScore: score, integrityLevel: calcLevel(score) };
      signalsRef.current = result;
      return result;
    });
  }, []);

  const sync = useCallback(async (force = false) => {
    if (!enabled || !endpoint) return;
    const merged = { ...signalsRef.current, ...gazeRef.current };
    const score = calcScore(merged);
    const payload = { ...merged, integrityScore: score, integrityLevel: calcLevel(score) };
    const serialized = JSON.stringify(payload);
    if (!force && serialized === lastSyncRef.current) return;
    try {
      const token = await getAuthToken();
      if (!token) return;
      await fetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: serialized,
      });
      lastSyncRef.current = serialized;
    } catch (_) {}
  }, [enabled, endpoint]);

  // ── Browser events ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    // 20-second grace period: ignore blur/visibility events that fire from the
    // browser's "Save password?" notification or the camera permission prompt.
    // Both appear within the first few seconds of the test starting and must
    // not count as violations.
    inGraceRef.current = true;
    clearTimeout(graceTimerRef.current);
    graceTimerRef.current = setTimeout(() => { inGraceRef.current = false; }, 20000);

    const onVisibility = () => {
      if (inGraceRef.current) return;
      if (document.hidden) update(
        (c) => addTimestamp({ ...c, tabSwitchCount: c.tabSwitchCount + 1 }, "tab_hidden"),
        "Tab switching is recorded. Please stay on the assessment tab."
      );
    };
    const onBlur = () => {
      if (inGraceRef.current) return;
      update(
        (c) => addTimestamp({ ...c, windowBlurCount: c.windowBlurCount + 1 }, "window_blur"),
        "Window focus lost — please keep the assessment window active."
      );
    };
    const onCopyPaste = (e) => update(
      (c) => addTimestamp({ ...c, copyPasteAttempts: c.copyPasteAttempts + 1 }, e.type),
      "Copy/paste is not allowed during this assessment."
    );
    const onFullscreenChange = () => {
      // Live state updates unconditionally — grace period only controls
      // whether this ALSO counts as a scored violation, not whether callers
      // can find out we're no longer in fullscreen. Gating this on grace too
      // would leave isFullscreenActive stuck at a stale "true" for the rest
      // of the test if the candidate happened to exit fullscreen in the
      // first 20s, since fullscreenchange won't fire again on its own.
      const active = !!document.fullscreenElement;
      setIsFullscreenActive(active);
      if (inGraceRef.current) return;
      if (!active) update(
        (c) => addTimestamp({ ...c, fullscreenExitCount: c.fullscreenExitCount + 1 }, "fullscreen_exit"),
        "You have exited fullscreen. Please return to fullscreen mode."
      );
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("copy", onCopyPaste);
    document.addEventListener("paste", onCopyPaste);
    document.addEventListener("cut", onCopyPaste);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    const syncInterval = setInterval(() => sync(false), 8000);
    return () => {
      clearTimeout(graceTimerRef.current);
      inGraceRef.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("copy", onCopyPaste);
      document.removeEventListener("paste", onCopyPaste);
      document.removeEventListener("cut", onCopyPaste);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      clearInterval(syncInterval);
      sync(true);
    };
  }, [enabled, sync, update]);

  // ── Screen-share stop detection ─────────────────────────────────────────
  useEffect(() => {
    if (!screenStream) { setIsScreenShareLive(true); return; }
    const track = screenStream.getVideoTracks()[0];
    if (!track) { setIsScreenShareLive(true); return; }
    setIsScreenShareLive(track.readyState === "live");
    const onEnded = () => {
      setIsScreenShareLive(false);
      if (!enabled) return;
      update(
        (c) => addTimestamp({ ...c, screenShareStoppedCount: c.screenShareStoppedCount + 1 }, "screen_share_stopped"),
        "Screen sharing has stopped. Proctoring requires an active screen share."
      );
    };
    track.addEventListener("ended", onEnded);
    return () => track.removeEventListener("ended", onEnded);
  }, [enabled, screenStream, update]);

  // ── Screen-share capability flag (all iOS browsers lack this API — see
  //    proctoringSupport.js) — a fixed device characteristic, not an incident,
  //    so it's set once and simply included in every sync from then on. ────
  useEffect(() => {
    if (!enabled || isScreenShareSupported()) return;
    update((c) => ({ ...c, screenShareUnsupported: true }));
  }, [enabled, update]);

  // ── Multi-display detection (Screen Details API — Chrome/Edge only) ─────
  useEffect(() => {
    if (!enabled) return;
    if (typeof window.getScreenDetails !== "function") return;
    let cancelled = false;
    let details = null;
    const onScreensChange = () => {
      if (cancelled || !details) return;
      update((c) => addTimestamp({
        ...c,
        multiDisplayDetected: details.screens.length > 1,
        multiDisplayChangedCount: c.multiDisplayChangedCount + 1,
      }, "multi_display_changed"), "A change in connected displays was detected.");
    };
    window.getScreenDetails()
      .then((sd) => {
        if (cancelled) return;
        details = sd;
        update((c) => ({
          ...c,
          multiDisplaySupported: true,
          multiDisplayDetected: sd.screens.length > 1,
        }));
        sd.addEventListener("screenschange", onScreensChange);
      })
      .catch(() => {
        // Unsupported, denied, or unavailable — silently skip, do not flag.
      });
    return () => {
      cancelled = true;
      details?.removeEventListener("screenschange", onScreensChange);
    };
  }, [enabled, update]);

  // ── Gaze / head-direction tracking via FaceMesh ─────────────────────────
  useEffect(() => {
    if (!enabled || !mediaStream) return;
    let cancelled = false;
    let meshCleanup = null;

    // Use a hidden video element — does not affect the visible self-view feed.
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = mediaStream;

    (async () => {
      try {
        setGazeStatus("loading");
        await video.play();
        const FaceMesh = await loadFaceMesh();
        if (cancelled || !FaceMesh) return;

        const mesh = new FaceMesh({
          // Must match the pinned version in loadFaceMesh() above — this is
          // where the library fetches its own WASM/model files, and a
          // version mismatch between the JS and its assets (e.g. if this had
          // stayed on unpinned "latest" while the script above got pinned,
          // or vice versa) is a known source of hard-to-diagnose internal
          // crashes in MediaPipe's solutions.
          locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/${f}`,
        });
        mesh.setOptions({
          maxNumFaces: 2,
          refineLandmarks: false,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });

        mesh.onResults((results) => {
          if (cancelled) return;
          const faces = results.multiFaceLandmarks || [];
          const g = gazeRef.current;
          let scored = false;

          if (faces.length === 0) {
            noFaceConsecRef.current += 1;
            if (noFaceConsecRef.current >= 4) {
              g.faceNotVisible += 1;
              noFaceConsecRef.current = 0;
              scored = true;
              showWarning("No face detected. Please stay in frame.");
            }
          } else {
            noFaceConsecRef.current = 0;
            if (faces.length > 1) {
              multiFaceConsecRef.current += 1;
              if (multiFaceConsecRef.current >= 3 && g.multipleFaces === 0) {
                g.multipleFaces = 1;
                scored = true;
                showWarning("Multiple faces detected. Please take this assessment alone.");
              }
            } else {
              multiFaceConsecRef.current = 0;
            }
            const dir = computeHeadDirection(faces[0]);
            if (dir && dir !== "center" && dir !== prevDirRef.current) {
              if (dir === "left") {
                g.gazeLookLeft += 1;
                scored = true;
                if (g.gazeLookLeft % 3 === 1) showWarning("Looking away from screen detected.");
              }
              if (dir === "right") {
                g.gazeLookRight += 1;
                scored = true;
                if (g.gazeLookRight % 3 === 1) showWarning("Looking away from screen detected.");
              }
              if (dir === "down") {
                g.gazeLookDown += 1;
                scored = true;
                if (g.gazeLookDown % 5 === 1) showWarning("Looking down from screen detected.");
              }
            }
            prevDirRef.current = dir || "center";
          }

          if (scored) flushGaze();
        });

        const interval = setInterval(() => {
          if (!cancelled && video.readyState >= 2) {
            mesh.send({ image: video }).catch(() => {});
          }
        }, 1000);

        meshCleanup = () => {
          clearInterval(interval);
          mesh.close();
        };
        setGazeStatus("active");
      } catch (err) {
        if (!cancelled) {
          console.warn("Gaze tracking unavailable:", err);
          setGazeStatus("unavailable");
        }
      }
    })();

    return () => {
      cancelled = true;
      meshCleanup?.();
      video.pause();
      video.srcObject = null;
    };
  }, [enabled, mediaStream, showWarning, flushGaze]);

  return {
    warning,
    integrityScore: signals.integrityScore,
    integrityLevel: signals.integrityLevel,
    gazeStatus,
    signals,
    isFullscreenActive,
    isScreenShareLive,
    flush: () => sync(true),
  };
}
